import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import * as R from 'ramda';

import {
  GET_BUDGET,
  GET_BUDGET_OPTIONS,
  GET_BUDGET_SUMMARIES,
  GET_BUDGET_UNIT,
} from '@atom/graph/budget';
import {
  BasicBudget,
  Budget,
  BudgetCategory,
  BudgetItemTemplate,
  BudgetsConnectionInput,
  BudgetSummary,
  BudgetSummaryConnectionInput,
  BudgetUnit,
} from '@atom/types/budget';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { BUDGET_OPTIONS_LIMIT } from './budgetDetailUtils';

export type FetchParentProps = {
  budgetUnitId: string;
  newCategoryIds?: string[];
  newBudgetItemTemplateNames?: string[];
  newBudgetTypes?: string[];
};

type BudgetDetailInit = {
  unitId?: string;
  showExpenditures: boolean;
  existingShowTracking: boolean;
  existingShowChart: boolean;
  existingComparisonBudgets: BasicBudget[];
  existingCategoryFilters: BudgetCategory[];
  existingBudgetItemFilters: BudgetItemTemplate[];
};

type UseBudgetDetailReturn = {
  budget: Budget;
  parentBudgetUnit: BudgetUnit;
  setParentBudgetUnit: React.Dispatch<React.SetStateAction<BudgetUnit>>;
  loadingParentUnit: boolean;
  fetchParentUnit: (props: FetchParentProps) => void;
  budgetSummaries: BudgetSummary[];
  setBudgetSummaries: React.Dispatch<React.SetStateAction<BudgetSummary[]>>;
  fetchSummaries: (options: {
    variables: { input: BudgetSummaryConnectionInput };
  }) => void;
  loadingSummaries: boolean;
  budgetOptions: Budget[];
  totalBudgetOptions: number;
  setTotalBudgetOptions: React.Dispatch<React.SetStateAction<number>>;
  loadingBudgetOptions: boolean;
  getBudgetOptions: (options: {
    variables: { input: BudgetsConnectionInput };
  }) => void;
  showTracking: boolean;
  setShowTracking: React.Dispatch<React.SetStateAction<boolean>>;
  showChart: boolean;
  setShowChart: React.Dispatch<React.SetStateAction<boolean>>;
  comparisonBudgets: BasicBudget[];
  setComparisonBudgets: React.Dispatch<React.SetStateAction<BasicBudget[]>>;
  categoryFilters: BudgetCategory[];
  setCategoryFilters: React.Dispatch<React.SetStateAction<BudgetCategory[]>>;
  categoryIds: string[];
  budgetItemTemplateFilters: BudgetItemTemplate[];
  setBudgetItemTemplateFilters: React.Dispatch<
    React.SetStateAction<BudgetItemTemplate[]>
  >;
  budgetItemTemplateNames: string[];
};

const useBudgetDetail = (
  budgetId: string,
  budgetTypes: string[],
  init: BudgetDetailInit,
): UseBudgetDetailReturn => {
  const [parentBudgetUnit, setParentBudgetUnit] = useState<BudgetUnit>(null);
  const [budgetSummaries, setBudgetSummaries] = useState<BudgetSummary[]>([]);
  const [budgetOptions, setBudgetOptions] = useState<Budget[]>([]);
  const [totalBudgetOptions, setTotalBudgetOptions] = useState<number>(0);
  const [showTracking, setShowTracking] = useState<boolean>(
    init.showExpenditures && init.existingShowTracking,
  );
  const [showChart, setShowChart] = useState<boolean>(
    init.showExpenditures && init.existingShowChart,
  );
  const [comparisonBudgets, setComparisonBudgets] = useState<BasicBudget[]>(
    init.existingComparisonBudgets,
  );
  const [categoryFilters, setCategoryFilters] = useState<BudgetCategory[]>(
    init.existingCategoryFilters,
  );
  const [budgetItemTemplateFilters, setBudgetItemTemplateFilters] = useState<
    BudgetItemTemplate[]
  >(init.existingBudgetItemFilters);

  const { data: budgetData } = useQuery(GET_BUDGET, {
    variables: { id: budgetId },
    fetchPolicy: 'network-only',
  });

  const budget: Budget = useMemo(
    () => R.pathOr(null, ['budget'], budgetData),
    [budgetData],
  );

  const categoryIds = useMemo(
    () => categoryFilters.map(({ id }) => id),
    [categoryFilters],
  );

  const budgetItemTemplateNames = useMemo(
    () => budgetItemTemplateFilters.map(({ name }) => name),
    [budgetItemTemplateFilters],
  );

  const [getParentUnit, { loading: loadingParentUnit, data: parentUnitData }] =
    useLazyQuery(GET_BUDGET_UNIT, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    if (parentUnitData !== undefined) {
      setParentBudgetUnit(R.pathOr(null, ['budgetUnit'], parentUnitData));
    }
  }, [parentUnitData]);

  const [fetchSummaries, { loading: loadingSummaries, data: summariesData }] =
    useLazyQuery(GET_BUDGET_SUMMARIES, { fetchPolicy: 'network-only' });

  useEffect(() => {
    if (summariesData) {
      setBudgetSummaries(
        R.pathOr([], ['budgetSummary', 'budgetSummary'], summariesData),
      );
    }
  }, [summariesData]);

  const [
    getBudgetOptions,
    { loading: loadingBudgetOptions, data: budgetOptionsData },
  ] = useLazyQuery(GET_BUDGET_OPTIONS, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    if (!isNilOrEmpty(budgetOptionsData)) {
      const nextOptions: Budget[] = R.pathOr(
        [],
        ['budgetOptions', 'budgets'],
        budgetOptionsData,
      );
      const newTotal: number = R.pathOr(
        0,
        ['budgetOptions', 'totalCount'],
        budgetOptionsData,
      );
      setBudgetOptions(prev => [...(prev || []), ...nextOptions]);
      setTotalBudgetOptions(newTotal);
    }
  }, [budgetOptionsData]);

  const fetchParentUnit = (parentParams: FetchParentProps) => {
    if (!loadingParentUnit) {
      const {
        budgetUnitId,
        newCategoryIds,
        newBudgetItemTemplateNames,
        newBudgetTypes,
      } = parentParams;

      getParentUnit({
        variables: {
          input: {
            budgetId,
            budgetUnitId,
            categoryIds: newCategoryIds ?? categoryIds,
            budgetItemTemplateNames:
              newBudgetItemTemplateNames ?? budgetItemTemplateNames,
            budgetTypes: newBudgetTypes ?? budgetTypes,
          },
        },
      });
    }
  };

  useEffect(() => {
    if (!isNilOrEmpty(budget)) {
      setBudgetOptions([]);
      setTotalBudgetOptions(0);
      setShowTracking(init.showExpenditures && init.existingShowTracking);
      setShowChart(init.showExpenditures && init.existingShowChart);
      setComparisonBudgets(init.existingComparisonBudgets);
      setCategoryFilters(init.existingCategoryFilters);
      setBudgetItemTemplateFilters(init.existingBudgetItemFilters);
      fetchParentUnit({
        budgetUnitId: init.unitId || budget?.rootBudgetUnitId,
        newCategoryIds: init.existingCategoryFilters.map(({ id }) => id),
        newBudgetItemTemplateNames: init.existingBudgetItemFilters.map(
          ({ name }) => name,
        ),
      });
      getBudgetOptions({
        variables: {
          input: {
            templateId: budget.templateId,
            page: 1,
            limit: BUDGET_OPTIONS_LIMIT,
            sortBy: 'name,asc',
          },
        },
      });
    }
  }, [budget]);

  return {
    budget,
    parentBudgetUnit,
    setParentBudgetUnit,
    loadingParentUnit,
    fetchParentUnit,
    budgetSummaries,
    setBudgetSummaries,
    fetchSummaries,
    loadingSummaries,
    budgetOptions,
    totalBudgetOptions,
    setTotalBudgetOptions,
    loadingBudgetOptions,
    getBudgetOptions,
    showTracking,
    setShowTracking,
    showChart,
    setShowChart,
    comparisonBudgets,
    setComparisonBudgets,
    categoryFilters,
    setCategoryFilters,
    categoryIds,
    budgetItemTemplateFilters,
    setBudgetItemTemplateFilters,
    budgetItemTemplateNames,
  };
};

export default useBudgetDetail;
